import { WeekDay, WEEKDAYS, WeeklySchedule } from '@core/models';

/**
 * A brand-new week: genuinely empty.
 *
 * There used to be a seeded one beside this — three categories and a night's
 * sleep, so the prototype demoed something without a server. It is gone
 * (standards §8c), and the reason is worth keeping: those category ids were
 * made-up strings, so the server refused the first activity saved against one
 * with `'cat-work' is not a valid UUID`.
 *
 * Somebody with no schedule yet has no categories either. They add one and it is
 * theirs, with a guid the server issued.
 */
export function emptyWeek(personaCode: string): WeeklySchedule {
  return { personaCode, authored: false, categories: [], activities: [] };
}

/** Minutes from midnight for a 24h `HH:mm` — the unit every schedule total is summed in. */
export function scheduleMinutes(hhmm: string): number {
  const [h, m] = hhmm.split(':').map(Number);
  return (h || 0) * 60 + (m || 0);
}

/**
 * "2h 30m", "45m", "3h" — a schedule duration as the Weekly Schedule writes it;
 * an em dash for none. One wording for the grid, the hours summary and MRP's
 * Weekly Cadence, so the same week reads the same everywhere it is shown.
 */
export function formatScheduleMinutes(m: number): string {
  if (!m) return '—';
  const h = Math.floor(m / 60);
  const rem = m % 60;
  if (h === 0) return `${rem}m`;
  if (rem === 0) return `${h}h`;
  return `${h}h ${rem}m`;
}

/** One category's planned time in a week: per weekday and for the whole week. */
export interface CategoryMinutes {
  readonly categoryGuid: string;
  /** The category's name, or "Uncategorised" for an activity whose category has gone. */
  readonly label: string;
  /** The palette swatch it wears (resolved to a hex by the caller, who holds the palette). */
  readonly colorGuid: string | null;
  readonly days: Record<WeekDay, number>;
  readonly week: number;
}

/**
 * THE hours-per-category rule for a weekly schedule — what the Weekly Schedule's
 * Hours Summary shows and what MRP's Weekly Cadence reads, so the two cannot
 * disagree about how much of the week a category takes.
 *
 * Summed from the activities themselves (end minus start, once per weekday the
 * activity is on), not by counting grid cells, so a total is the activity's real
 * length rather than however many rows it was drawn across. Categories with no
 * time are left out; the rest are largest first.
 */
export function minutesByCategory(week: WeeklySchedule): CategoryMinutes[] {
  const byGuid = new Map(week.categories.map((c) => [c.categoryGuid, c]));
  const totals = new Map<string, { label: string; colorGuid: string | null; days: Record<WeekDay, number>; week: number }>();
  for (const a of week.activities) {
    const minutes = scheduleMinutes(a.endTime) - scheduleMinutes(a.startTime);
    if (minutes <= 0) continue;
    let t = totals.get(a.categoryGuid);
    if (!t) {
      const category = byGuid.get(a.categoryGuid);
      t = {
        label: category?.label ?? 'Uncategorised',
        colorGuid: category?.colorGuid ?? null,
        days: Object.fromEntries(WEEKDAYS.map((d) => [d, 0])) as Record<WeekDay, number>,
        week: 0,
      };
      totals.set(a.categoryGuid, t);
    }
    for (const day of a.weekdays) {
      t.days[day] += minutes;
      t.week += minutes;
    }
  }
  return [...totals.entries()]
    .map(([categoryGuid, t]) => ({ categoryGuid, ...t }))
    .filter((t) => t.week > 0)
    .sort((a, b) => b.week - a.week);
}
